import { z } from 'zod';
import { badgeDisplayListSchema } from './badge-display-list.js';
import { resolvedBadgePrinterDTOSchema } from './badge-printer.js';
import { accentColorSchema } from './kiosk-branding.js';
/**
 * ONE drawable badge, already resolved down the BDG-01 ladder (visitor type -> location ->
 * tenant -> built-in) for one scope. TEMPLATE-LEVEL, never per visit: adr/0024 keeps the
 * display list a template artifact precisely so offline printing is unaffected.
 *
 * S7 allow-list: NO `logoFileId`, NO URL, NO `templateId`, NO `tenantId`, NO `locationId`. The
 * kiosk needs none of them — `hasLogo` plus a separate byte route is the whole logo contract,
 * exactly as `DeviceBranding.hasLogo` already works for kiosk branding.
 *
 * NO media table and NO model enum. The media geometry the kiosk needs is already resolved on
 * `DeviceBadgeConfigurationDTO.printer` (`printableDotsWidth` / `printableDotsHeight` /
 * `colorPlanes` / `supportsTwoColor`), and the *layout* canvas is already on `displayList`. A
 * task that adds a Dart media table or a Dart model-capability table is wrong by construction
 * (`docs/adr/0023` 2026-09-15 amendment, milestone §Technical Notes) — this is the object that
 * would tempt one, hence the note here.
 */
export const deviceBadgeTemplateDTOSchema = z
    .object({
    /** The visitor type this badge applies to, or `null` for the location's default badge —
     *  the one a visit with no visitor type, or with a type that has no template of its own,
     *  uses. Exactly one entry in a configuration carries `null`. */
    visitorTypeId: z.string().min(1).nullable(),
    /** What to draw. The only thing on this object with any geometry in it. */
    displayList: badgeDisplayListSchema,
    /** What the drawer resolves the `ACCENT` fill role to. Lowercase `#rrggbb`, or null when
     *  the template has no accent (in which case the list carries no ACCENT item either). */
    accentColor: accentColorSchema.nullable(),
    /** Whether a logo exists for this badge. A BOOLEAN, never a file id and never a URL
     *  (`DeviceSessionDTO`'s own rule). Bytes come from the separate logo route. */
    hasLogo: z.boolean(),
    /** RND-06. Minutes from check-in to expiry, or null for no expiry. The kiosk computes both
     *  the printed `EXPIRES_AT` text and the QR's `exp` from this and its own check-in instant —
     *  the server cannot, because the visit does not exist when this is synced. */
    validityMinutes: z.number().int().nullable(),
})
    .strict();
/** Machine-readable error code for a configuration whose `badges` array carries more than one
 *  entry for the same scope (including two `null` entries) — a server bug, never a valid
 *  payload. Raised at path `['badges']`. */
export const DUPLICATE_BADGE_SCOPE = 'DUPLICATE_BADGE_SCOPE';
/**
 * The whole answer to "what does this kiosk need in order to print, before it needs to print"
 * (PRT-04, RND-01a). `printer: null` is a supported state (PRT-12), not an error.
 *
 * NO fingerprint, no ETag, no revision. Deliberate: the kiosk decides a logo refetch is needed
 * by comparing this whole payload against the one it cached, which is exact and costs nothing
 * on the wire. S35-TASK-047 implements that.
 *
 * `printingEnabled` is ONLY the LOCATION half of the badge-printing AND gate
 * (`KioskSetting.badgePrintingEnabled`), resolved once for the whole configuration — it is
 * NOT combined with any visitor-type gate here. The visitor-type half lives on
 * `ResolvedVisitorType.badgePrintingEnabled` in the check-in bundle
 * (`GET /api/device/v1/check-in-bundle`), keyed per visitor type, because this document's
 * `badges[]` is keyed by "which visitor types have their own custom badge TEMPLATE", not
 * "every visitor type at the location" — a type with printing disabled but no custom
 * template would never appear in `badges[]`. A CONSUMER MUST AND BOTH FIELDS TOGETHER
 * BEFORE PRINTING: `printingEnabled && resolvedVisitorType.badgePrintingEnabled`. Neither
 * field alone is the answer to "should this badge print".
 */
export const deviceBadgeConfigurationDTOSchema = z
    .object({
    printer: resolvedBadgePrinterDTOSchema.nullable(),
    /** Always at least one entry — the ladder bottoms out at the built-in template, so the
     *  `visitorTypeId: null` default always exists. */
    badges: z.array(deviceBadgeTemplateDTOSchema).min(1),
    /** The location's own badge-printing gate ONLY. See the schema's doc comment above — a
     *  consumer must AND this with `ResolvedVisitorType.badgePrintingEnabled`. */
    printingEnabled: z.boolean(),
})
    .strict()
    .superRefine((value, ctx) => {
    const seen = new Set();
    for (const badge of value.badges) {
        if (seen.has(badge.visitorTypeId)) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: DUPLICATE_BADGE_SCOPE,
                path: ['badges'],
            });
            return;
        }
        seen.add(badge.visitorTypeId);
    }
});
/** Picks the badge for a visit, per the BDG-01 ladder as the kiosk sees it: the visitor type's
 *  own badge if the configuration carries one, else the default. PURE, and exported here rather
 *  than left to each caller so the server, the kiosk and any future consumer choose identically. */
export function selectDeviceBadge(configuration, visitorTypeId) {
    if (visitorTypeId !== null) {
        const scoped = configuration.badges.find((badge) => badge.visitorTypeId === visitorTypeId);
        if (scoped)
            return scoped;
    }
    const fallback = configuration.badges.find((badge) => badge.visitorTypeId === null);
    if (!fallback) {
        throw new Error('DeviceBadgeConfigurationDTO must always carry a visitorTypeId: null badge');
    }
    return fallback;
}
//# sourceMappingURL=device-badge-configuration.js.map